import Box from '@mui/material/Box';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { ProjectMaterialTotal, ProjectSummaryBasic } from '~hooks/useProjects/models';
import { Nullable } from '~types/Nullable';
import { toDecimal } from '~utils/decimal';

interface ProjectMaterialProgressProps {
  materialTotals: ProjectMaterialTotal[];
  projectSummary: Nullable<ProjectSummaryBasic>;
}

const formatQuantity = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Compact delivered-material tracker for the order form's Project Details.
 * Always shows the delivered quantity when the project has a summary; adds the
 * "delivered / total" comparison and inline progress bar only when the project
 * defines a material total in the same unit of measure as the delivered total.
 */
export default function ProjectMaterialProgress({
  materialTotals,
  projectSummary,
}: ProjectMaterialProgressProps) {
  if (!projectSummary) {
    return null;
  }

  const deliveredQuantity = projectSummary.totalQuantity;
  const deliveredUnitOfMeasure = projectSummary.totalQuantityUnitOfMeasure;

  const projectMaterialTotal = deliveredUnitOfMeasure
    ? materialTotals
        .filter((total) => total.unitOfMeasure?.id === deliveredUnitOfMeasure.id)
        .reduce((sum, total) => sum.plus(toDecimal(total.totalQuantity)), toDecimal(0))
        .toNumber()
    : 0;

  const hasProjectMaterialTotal = projectMaterialTotal > 0;
  const deliveredPercent = hasProjectMaterialTotal
    ? Math.min(100, Math.round((deliveredQuantity / projectMaterialTotal) * 100))
    : 0;

  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        borderTop: `1px solid ${theme.brandV2.colors.treadGray7}`,
        display: 'flex',
        gap: 1.5,
        mt: 2,
        pt: 2,
      })}
    >
      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray4,
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        })}
      >
        {t('projects.summary.delivered')}
      </Typography>

      <Typography
        component="span"
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: '14px',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        })}
      >
        {formatQuantity(deliveredQuantity)}
        {hasProjectMaterialTotal && (
          <Box
            component="span"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray4,
              fontWeight: 500,
            })}
          >
            {` / ${formatQuantity(projectMaterialTotal)}`}
          </Box>
        )}
        {deliveredUnitOfMeasure && (
          <Box
            component="span"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray3,
              fontSize: '12px',
              fontWeight: 500,
              ml: 0.5,
            })}
          >
            {deliveredUnitOfMeasure.name}
          </Box>
        )}
      </Typography>

      {hasProjectMaterialTotal && (
        <>
          <LinearProgress
            value={deliveredPercent}
            variant="determinate"
            sx={(theme) => ({
              borderRadius: 1,
              flexGrow: 1,
              height: '6px',
              [`&.${linearProgressClasses.root}`]: {
                bgcolor: theme.brandV2.colors.treadGray7,
              },
              [`& .${linearProgressClasses.bar}`]: {
                bgcolor: theme.brandV2.colors.treadBlue,
              },
            })}
          />
          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray3,
              fontSize: '12px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            })}
          >
            {t('projects.summary.percent', { percent: deliveredPercent })}
          </Typography>
        </>
      )}
    </Box>
  );
}
